/**
 * Design Agent WebSocket Service
 *
 * Provides real-time WebSocket connections for Design Agent
 * to enable multi-window synchronization and persistent streaming notifications.
 *
 * Events handled:
 * - design_user_message_added: When a user message is saved
 * - design_streaming_started: When AI streaming begins
 * - design_streaming_completed: When AI streaming completes
 * - design_streaming_cancelled: When user cancels streaming
 */

import { getApiBaseUrl as getHttpApiBaseUrl } from '../utils/axios';

// Determine API base URL for WebSocket connections
const getWsBaseUrl = () => {
  const httpApiBase = getHttpApiBaseUrl();
  const httpOrigin = httpApiBase.replace(/\/?api$/, '');
  const wsOrigin = httpOrigin.replace(/^http/, 'ws');

  console.log('🔍 Design WebSocket URL construction:', {
    httpApiBase,
    httpOrigin,
    wsOrigin,
  });

  return wsOrigin;
};

// Design-specific WebSocket message types
export type DesignWebSocketEventType =
  | 'design_user_message_added'
  | 'design_streaming_started'
  | 'design_streaming_completed'
  | 'design_streaming_cancelled'
  | 'design_streaming_status'
  | 'connection_established'
  | 'streaming_status_response'
  | 'conversation_state_response'
  | 'pong'
  | 'error'
  | 'design_streaming_cost_update_on_cancellation';

export interface DesignWebSocketMessage {
  type: DesignWebSocketEventType;
  data: {
    message_id?: string;
    project_id?: string;
    content?: string;
    streaming_state_id?: string;
    assistant_message_id?: string;
    status?: string;
    is_streaming?: boolean;
    timestamp?: string;
    message?: string;
    user_info?: {
      id: string;
      email?: string;
      username?: string;
      firstName?: string;
      lastName?: string;
    };
  };
  timestamp: string;
  original_ping_timestamp?: string;
}

export interface DesignWebSocketCallbacks {
  onMessage?: (message: DesignWebSocketMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
  onReconnectAttempt?: (attempt: number) => void;
}

export class DesignWebSocket {
  private ws: WebSocket | null = null;
  private projectId: string;
  private token: string;
  private callbacks: DesignWebSocketCallbacks;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = Infinity;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 60000;
  private isConnecting = false;
  private shouldReconnect = true;
  private reconnectResetInterval: ReturnType<typeof setInterval> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private networkStatusListener: (() => void) | null = null;
  private pongTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPong = Date.now();
  private hasConnectedBefore = false;
  private visibilityChangeListener: (() => void) | null = null;
  private isPageVisible = true;
  private disconnectionReason = '';
  private consecutivePingFailures = 0;
  private maxPingFailures = 3;

  constructor(projectId: string, token: string, callbacks: DesignWebSocketCallbacks = {}) {
    this.projectId = projectId;
    this.token = token;
    this.callbacks = callbacks;
  }

  updateToken(token: string): void {
    this.token = token;
  }

  updateCallbacks(callbacks: DesignWebSocketCallbacks) {
    this.callbacks = callbacks;
  }

  async connect(): Promise<void> {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.isConnecting = true;

    try {
      const baseUrl = getWsBaseUrl();
      const wsUrl = `${baseUrl}/api/design-agent/ws/${this.projectId}?token=${this.token}`;

      console.log('🔌 [Design] Attempting WebSocket connection:', {
        baseUrl,
        wsUrl,
        projectId: this.projectId,
      });

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('🔌 [Design] WebSocket connected for project:', this.projectId);
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this.stopReconnectResetInterval();
        this.startPingPong();
        this.setupNetworkStatusListener();
        this.setupVisibilityListener();

        if (this.hasConnectedBefore) {
          console.log('🔄 [Design] WebSocket reconnected for project:', this.projectId);
          this.callbacks.onReconnect?.();
        } else {
          console.log('✅ [Design] WebSocket initial connection for project:', this.projectId);
          this.hasConnectedBefore = true;
          this.callbacks.onConnect?.();
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const message: DesignWebSocketMessage = JSON.parse(event.data);
          console.log('📨 [Design] WebSocket message:', message.type, message);

          // Handle pong messages
          if (message.type === 'pong') {
            this.lastPong = Date.now();
            this.consecutivePingFailures = 0;
            console.log('🏓 [Design] Pong received');
            if (this.pongTimeout) {
              clearTimeout(this.pongTimeout);
              this.pongTimeout = null;
            }
            return;
          }

          // Handle connection established
          if (message.type === 'connection_established') {
            console.log('✅ [Design] Connection established for project:', this.projectId);
            return;
          }

          // Pass messages to callback
          this.callbacks.onMessage?.(message);
        } catch (error) {
          console.error('[Design] Error parsing WebSocket message:', error);
        }
      };

      this.ws.onclose = (event) => {
        const reason = event.reason || this.disconnectionReason || 'Unknown';
        console.log('🔌 [Design] WebSocket disconnected:', {
          projectId: this.projectId,
          code: event.code,
          reason,
          wasClean: event.wasClean,
        });

        this.isConnecting = false;
        this.stopPingPong();
        this.disconnectionReason = '';
        this.consecutivePingFailures = 0;
        this.callbacks.onDisconnect?.();

        // Don't reconnect for authentication errors (4001-4003) or normal closure (1000)
        const isAuthError = event.code >= 4001 && event.code <= 4003;
        const isNormalClosure = event.code === 1000 && reason !== 'Ping/pong failure';
        
        if (!isAuthError && !isNormalClosure && this.shouldReconnect) {
          this.scheduleReconnect();
        } else if (isAuthError) {
          console.error('❌ [Design] Authentication error, stopping reconnection attempts:', reason);
        }
      };

      this.ws.onerror = (error) => {
        console.error('❌ [Design] WebSocket error:', error);
        this.isConnecting = false;
        this.callbacks.onError?.(error);

        if (this.shouldReconnect) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      console.error('[Design] Error creating WebSocket:', error);
      this.isConnecting = false;
      this.callbacks.onError?.(error as Event);

      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 6)),
      this.maxReconnectDelay
    );

    console.log(`🔄 [Design] Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    this.callbacks.onReconnectAttempt?.(this.reconnectAttempts);
    this.startReconnectResetInterval();

    setTimeout(() => {
      if (this.shouldReconnect) {
        console.log(`🔄 [Design] Attempting reconnection ${this.reconnectAttempts}`);
        // Refresh token from localStorage before reconnecting
        const freshToken = localStorage.getItem('token') || '';
        if (freshToken && freshToken !== this.token) {
          console.log('🔑 [Design] Refreshing token for reconnection');
          this.token = freshToken;
        }
        this.connect();
      }
    }, delay);
  }

  private startReconnectResetInterval(): void {
    if (this.reconnectResetInterval) return;

    this.reconnectResetInterval = setInterval(() => {
      if (this.reconnectAttempts > 0) {
        console.log(`🔄 [Design] Resetting reconnection attempts`);
        this.reconnectAttempts = Math.max(0, this.reconnectAttempts - 5);
      }
    }, 10 * 60 * 1000);
  }

  private stopReconnectResetInterval(): void {
    if (this.reconnectResetInterval) {
      clearInterval(this.reconnectResetInterval);
      this.reconnectResetInterval = null;
    }
  }

  private setupNetworkStatusListener(): void {
    if (this.networkStatusListener || typeof navigator === 'undefined') return;

    this.networkStatusListener = () => {
      console.log('🌐 [Design] Network restored, attempting reconnection');
      if (this.shouldReconnect && (!this.ws || this.ws.readyState !== WebSocket.OPEN)) {
        this.reconnectAttempts = 0;
        this.consecutivePingFailures = 0;
        this.connect();
      }
    };

    window.addEventListener('online', this.networkStatusListener);
  }

  private removeNetworkStatusListener(): void {
    if (this.networkStatusListener) {
      window.removeEventListener('online', this.networkStatusListener);
      this.networkStatusListener = null;
    }
  }

  private setupVisibilityListener(): void {
    if (this.visibilityChangeListener || typeof document === 'undefined') return;

    this.visibilityChangeListener = () => {
      this.isPageVisible = !document.hidden;
      console.log(`👁️ [Design] Page visibility: ${this.isPageVisible ? 'visible' : 'hidden'}`);

      if (this.isPageVisible) {
        this.consecutivePingFailures = 0;
      }
    };

    document.addEventListener('visibilitychange', this.visibilityChangeListener);
  }

  private removeVisibilityListener(): void {
    if (this.visibilityChangeListener) {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
      this.visibilityChangeListener = null;
    }
  }

  private startPingPong(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        if (!this.isPageVisible) {
          console.log('⏸️ [Design] Skipping ping while page hidden');
          return;
        }
        this.sendPing();
      }
    }, 30000);
  }

  private stopPingPong(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  private sendPing(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        const pingMessage = {
          type: 'ping',
          timestamp: new Date().toISOString(),
        };
        this.ws.send(JSON.stringify(pingMessage));
        console.log('🏓 [Design] Ping sent');

        this.pongTimeout = setTimeout(() => {
          this.consecutivePingFailures++;
          console.warn(`⚠️ [Design] No pong (${this.consecutivePingFailures}/${this.maxPingFailures})`);

          if (this.consecutivePingFailures >= this.maxPingFailures) {
            console.error(`❌ [Design] Too many ping failures, closing connection`);
            this.disconnectionReason = 'Ping/pong failure';
            this.ws?.close(1000, 'Ping/pong failure');
          }
        }, 10000);
      } catch (error) {
        console.error('❌ [Design] Error sending ping:', error);
        this.consecutivePingFailures++;
      }
    }
  }

  sendMessage(message: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[Design] WebSocket not connected, cannot send message');
    }
  }

  requestStreamingStatus(): void {
    this.sendMessage({ type: 'get_streaming_status' });
  }

  requestConversationState(): void {
    this.sendMessage({ type: 'request_conversation_state' });
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.stopPingPong();
    this.stopReconnectResetInterval();
    this.removeNetworkStatusListener();
    this.removeVisibilityListener();
    this.disconnectionReason = 'User disconnected';

    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isOpenOrConnecting(): boolean {
    if (!this.ws) return false;
    return this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING;
  }

  getConnectionState(): 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'UNKNOWN' {
    if (!this.ws) return 'CLOSED';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

// WebSocket manager for Design connections
export class DesignWebSocketManager {
  private connections: Map<string, DesignWebSocket> = new Map();

  connect(projectId: string, token: string, callbacks: DesignWebSocketCallbacks = {}): DesignWebSocket {
    const existing = this.connections.get(projectId);
    if (existing && existing.isOpenOrConnecting()) {
      existing.updateCallbacks(callbacks);
      return existing;
    }

    if (existing) {
      existing.disconnect();
      this.connections.delete(projectId);
    }

    const ws = new DesignWebSocket(projectId, token, callbacks);
    this.connections.set(projectId, ws);

    ws.connect().catch((error) => {
      console.error('[Design] Failed to connect WebSocket:', projectId, error);
    });

    return ws;
  }

  disconnect(projectId: string): void {
    const ws = this.connections.get(projectId);
    if (ws) {
      ws.disconnect();
      this.connections.delete(projectId);
    }
  }

  getConnection(projectId: string): DesignWebSocket | undefined {
    return this.connections.get(projectId);
  }

  disconnectAll(): void {
    this.connections.forEach((ws) => {
      ws.disconnect();
    });
    this.connections.clear();
  }

  getConnectionCount(): number {
    return this.connections.size;
  }

  getConnectionStates(): Record<string, string> {
    const states: Record<string, string> = {};
    this.connections.forEach((ws, projectId) => {
      states[projectId] = ws.getConnectionState();
    });
    return states;
  }
}

// Global Design WebSocket manager instance
export const designWebsocketManager = new DesignWebSocketManager();
